import React, { createContext, useContext, useState, useEffect } from 'react';

const ProjectProgressContext = createContext();

export const useProjectProgress = () => {
  const context = useContext(ProjectProgressContext);
  if (!context) {
    throw new Error('useProjectProgress must be used within a ProjectProgressProvider');
  }
  return context;
};

export const ProjectProgressProvider = ({ children }) => {
  const [progress, setProgress] = useState({});

  // Load progress from localStorage on mount
  useEffect(() => {
    const savedProgress = localStorage.getItem('projectProgress');
    if (savedProgress) {
      try {
        setProgress(JSON.parse(savedProgress));
      } catch (error) {
        console.error('Error loading project progress:', error);
      }
    }
  }, []);

  // Save progress to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('projectProgress', JSON.stringify(progress));
  }, [progress]);

  const initializeProject = (projectId) => {
    if (!progress[projectId]) {
      setProgress(prev => ({
        ...prev,
        [projectId]: {
          currentPhase: 'brd',
          unlockedPhases: ['brd'],
          unlockedModules: {
            brd: ['overview'],
            uiux: ['overview'],
            architectural: ['overview'],
            'code-development': ['overview'],
            testing: [],
            deployment: []
          },
          completedModules: {
            brd: [],
            uiux: [],
            architectural: [],
            'code-development': [],
            testing: [],
            deployment: []
          }
        }
      }));
    }
  };

  const unlockNextPhase = (projectId, currentPhase) => {
    const phases = ['brd', 'uiux', 'architectural', 'code-development', 'testing', 'deployment'];
    const currentIndex = phases.indexOf(currentPhase);
    
    if (currentIndex < phases.length - 1) {
      const nextPhase = phases[currentIndex + 1];
      setProgress(prev => ({
        ...prev,
        [projectId]: {
          ...prev[projectId],
          unlockedPhases: [...new Set([...prev[projectId].unlockedPhases, nextPhase])],
          unlockedModules: {
            ...prev[projectId].unlockedModules,
            [nextPhase]: ['overview']
          }
        }
      }));
    }
  };

  const unlockNextModule = (projectId, phase, currentModule) => {
    const moduleOrder = {
      brd: ['overview', 'functional-requirements', 'non-functional-requirements', 'user-stories', 'conclusion'],
      uiux: ['overview', 'design-system', 'customer-pages', 'admin-pages', 'navigation-flow', 'conclusion'],
      architectural: ['overview', 'system-architecture', 'database-design', 'api-design', 'security-architecture', 'conclusion'],
      'code-development': ['overview', 'frontend-development', 'backend-development', 'database-implementation', 'testing', 'conclusion'],
      testing: ['overview', 'test-planning', 'unit-testing', 'integration-testing', 'performance-testing', 'conclusion'],
      deployment: ['overview', 'deployment-planning', 'environment-setup', 'final-steps']
    };

    const modules = moduleOrder[phase] || [];
    const currentIndex = modules.indexOf(currentModule);
    
    if (currentIndex < modules.length - 1) {
      const nextModule = modules[currentIndex + 1];
      setProgress(prev => ({
        ...prev,
        [projectId]: {
          ...prev[projectId],
          unlockedModules: {
            ...prev[projectId].unlockedModules,
            [phase]: [...new Set([...prev[projectId].unlockedModules[phase], nextModule])]
          }
        }
      }));
    }
  };

  const completeModule = (projectId, phase, module) => {
    setProgress(prev => ({
      ...prev,
      [projectId]: {
        ...prev[projectId],
        completedModules: {
          ...prev[projectId].completedModules,
          [phase]: [...new Set([...prev[projectId].completedModules[phase], module])]
        }
      }
    }));
  };

  const isPhaseUnlocked = (projectId, phase) => {
    return progress[projectId]?.unlockedPhases?.includes(phase) || false;
  };

  const isModuleUnlocked = (projectId, phase, module) => {
    return progress[projectId]?.unlockedModules?.[phase]?.includes(module) || false;
  };

  const isModuleCompleted = (projectId, phase, module) => {
    return progress[projectId]?.completedModules?.[phase]?.includes(module) || false;
  };

  const getCurrentPhase = (projectId) => {
    return progress[projectId]?.currentPhase || 'brd';
  };

  const setCurrentPhase = (projectId, phase) => {
    setProgress(prev => ({
      ...prev,
      [projectId]: {
        ...prev[projectId],
        currentPhase: phase
      }
    }));
  };

  const value = {
    progress,
    initializeProject,
    unlockNextPhase,
    unlockNextModule,
    completeModule,
    isPhaseUnlocked,
    isModuleUnlocked,
    isModuleCompleted,
    getCurrentPhase,
    setCurrentPhase
  };

  return (
    <ProjectProgressContext.Provider value={value}>
      {children}
    </ProjectProgressContext.Provider>
  );
};
